/*
    RequestPasswordReset page. Utilises server-side rendering. 
    Following considerations implemented:
        - If user is already logged in, they should not be able to access page -> redirect to profile page instead
        - If email is correct & in the system, send the password reset link
*/

// import external
import React from "react";
import firebase from "firebase/app";
import "firebase/auth";
import nookies from "nookies";

// import (internal)
import PageDefault from "../components/PageDefault";
import ErrorBlockMessage from "../components/appUI/ErrorBlockMessage";
import SuccessBlockMessage from "../components/appUI/SuccessBlockMessage";
import { verifyIdToken } from "../components/auth/FirebaseAdmin";
import { firebaseClient } from "../components/auth/FirebaseClient";
import DottedBg from "../components/pageUI/DottedBg";
import SummerEyesIcon from "../components/branding/SummerEyesIcon";
import SummerEyesButton from "../components/branding/SummerEyesButton";

// TODO: Incorporate all the nice Next.js stuff in to this page
import Head from "next/head";
import Link from "next/link";
import Image from "next/image";

/* 
    Server Side Rendering hook:
        - Create a check on server side to see if user is alredy logged in
        - If logged in, redirect to user profile page
*/
export async function getServerSideProps(context) {
  try {
    const cookies = nookies.get(context); // get the cookies

    // if no auth token in the cookies, proceed as normal
    if (!cookies.token) {
      return { props: {} };
    }

    const token = await verifyIdToken(cookies.token); // verify if the token is legitimate or not
    const { uid, email } = token;

    // if auth token exists in the cookies, push them to the profile page, and log it
    if (token) {
      context.res.writeHead(303, { location: "/authReqPage" });
      context.res.end();
      console.log(
        "!! UNINTENDED_USER_BEHAVIOUR: Logged-in user tried to access the password reset request page"
      );
      return { props: {} };
    } else {
      return { props: {} };
    }
  } catch (err) {
    context.res.writeHead(302, { location: "/" }); // redirect to home if errors - I don't want weird stuff happening in my login page
    context.res.end();
    console.log(
      "!! UNHANDLED_ERROR: Occurring when trying to check if user should be shown password reset request page. " +
        err
    );
    return { props: {} };
  }
}

/*
    generateErrorMessage(error): Handles an invalid password reset attempt and triggers the ErrorBlockMessage component
        - error: The error object from the Firebase auth function
        - Assesses whether the error is handled (e.g., validation, or not)
*/
function generateErrorMessage(error) {
  // if the error is handled, return a nice/custom message
  if (error.code === "auth/user-not-found") {
    return "Email not registered with an associated account";
  } else if (error.code === "auth/invalid-email") {
    return "Email entered is incorrect";
  } else if (error.code === "auth/user-disabled") {
    return "Your account is not active - please contact customer support";
  } else if (error.code === "auth/too-many-requests") {
    return "Too many request attempts. Please try again later";
  } else {
    // if not handled, return the error message
    return "Unexpected password reset error. " + error.message;
  }
}

export default function RequestPasswordReset({ session }) {
  firebaseClient();

  const [email, setEmail] = React.useState("");

  const [errorMessage, setErrorMessage] = React.useState("");
  const [successMessage, setSuccessMessage] = React.useState("");

  // method to attempt password reset email trigger
  const submitResetRequest = async (event, path) => {
    event.preventDefault();
    await firebase
      .auth()
      .sendPasswordResetEmail(email)
      .then(function () {
        setErrorMessage("");
        setSuccessMessage("Password reset request submitted");
      })
      .catch(function (error) {
        setErrorMessage(generateErrorMessage(error)); // create a nice string message & trigger the ErrorMessageBlock
      });
  };

  return (
    <>
      <PageDefault pageTitle="Reset password | SummerEyes">
        <DottedBg />
        {
          //Taken from TailwindUI Components
        }
        <div className="flex min-h-full items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
          <div className="w-full max-w-md space-y-8 ">
            <div>
              <div className="mx-auto h-16 w-16">
                <SummerEyesIcon className="h-16 w-16" lightMode />
              </div>
              <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
                Reset your account's password
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600">
                Or
                <Link href="/register">
                  <a className="font-medium text-summer-bright hover:text-summer-bright">
                    {" "}
                    sign in to your account{" "}
                  </a>
                </Link>
              </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
              <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                <ErrorBlockMessage errorState={errorMessage} />
                <SuccessBlockMessage
                  successState={successMessage}
                  redirectLink="/login"
                />
                {!successMessage && ( // stop showing the form elements if the success message is showing
                  <form className="space-y-6">
                    <input type="hidden" name="remember" value="true" />
                    <div className="-space-y-px rounded-md shadow-sm">
                      <div>
                        <label htmlFor="email-address" className="sr-only">
                          Email address
                        </label>
                        <input
                          id="email-address"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          className="focus:ring-summer-blue-500 focus:border-summer-blue-500 relative block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:outline-none sm:text-sm"
                          placeholder="Email address"
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                    </div>
                    <SummerEyesButton
                      full
                      designStyle="positivePath"
                      onClick={(event) => submitResetRequest(event, "/")}
                      customTailwind="group"
                    >
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                        {
                          //<!-- Heroicon name: solid/lock-closed
                        }
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      </span>
                      Request password reset link
                    </SummerEyesButton>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </PageDefault>
    </>
  );
}
